/**
 * Web performance metrics reporter.
 *
 * Buffers metrics and flushes them to the Hono BFF through the SHARED
 * telemetry transport (`@letsgethome/lib/telemetry/transport`) — the same
 * sendBeacon/keepalive-fetch delivery and tab-teardown flush the client logger
 * uses, rather than a second verbatim copy of it. The BFF ingests them as
 * structured logs with { source: 'browser-metrics' }.
 *
 * The generated `client-metrics_gen.ts` calls `reportMetric` with a
 * typed metric name, numeric value, and label bag.
 */
import { create, toJsonString } from '@bufbuild/protobuf';
import { TELEMETRY_MAX_BATCH_EVENTS } from '@letsgethome/lib/telemetry';
import {
  METRIC_FLUSH_INTERVAL_MS,
  registerFlushLifecycle,
  sendTelemetryBeacon,
} from '@letsgethome/lib/telemetry/transport';
import { BrowserMetricBatchSchema } from '@letsgethome/proto/telemetry/v1/telemetry_pb';

export interface MetricLabels {
  [label: string]: string;
}

// In-memory buffer item. Mirrors the proto `telemetry.v1.BrowserMetricEvent`
// field set; the buffer is converted to that message (via `create` +
// `toJsonString`) at flush, so the wire is proto3-canonical JSON.
interface MetricEvent {
  name: string;
  value: number;
  labels?: MetricLabels;
  ts: number;
  url: string;
}

// Flush threshold = the ingest endpoint's per-request cap, ONE shared
// number (Rust `domain_core::telemetry` SSOT) — see server/api/telemetry.ts.
const MAX_BUFFER_SIZE = TELEMETRY_MAX_BATCH_EVENTS;
const METRICS_ENDPOINT = '/api/telemetry/metrics';

let buffer: MetricEvent[] = [];
let lifecycleRegistered = false;

function flush(): void {
  if (buffer.length === 0) return;

  const batch = create(BrowserMetricBatchSchema, {
    metrics: buffer.map((m) => ({
      name: m.name,
      value: m.value,
      ...(m.labels !== undefined ? { labels: m.labels } : {}),
      ts: BigInt(m.ts),
      url: m.url,
    })),
  });
  buffer = [];

  const payload = toJsonString(BrowserMetricBatchSchema, batch);
  sendTelemetryBeacon(METRICS_ENDPOINT, new Blob([payload], { type: 'application/json' }));
}

// `registerFlushLifecycle` is a page-lifetime singleton (see its docs), so the
// first `reportMetric` arms it and later calls are a no-op.
function ensureFlush(): void {
  if (lifecycleRegistered || typeof window === 'undefined') return;
  lifecycleRegistered = true;
  registerFlushLifecycle(flush, METRIC_FLUSH_INTERVAL_MS);
}

export function reportMetric(name: string, value: number, labels?: MetricLabels): void {
  if (typeof window === 'undefined') return;

  ensureFlush();
  buffer.push({
    name,
    value,
    labels,
    ts: Date.now(),
    url: location.pathname,
  });

  if (buffer.length >= MAX_BUFFER_SIZE) flush();
}
